import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { Constants, getValidSelectValue, getZeroOption, type Option } from '@/src/utils';
import {
    type InitialOptions,
    type ClassOption,
    type SchoolClassOptions,
    type ExamOption,
    type FilterState,
} from '../models/filterDTOs';
import {
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    SimpleSingleSelect,
    FilterBarContainer,
} from '@/src/ui';

interface AttemptDetailsFiltersProps {
    refreshTableData: (filters: FilterState) => void | Promise<void>;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
}

// --- Options ---
const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectClassOption = getZeroOption('Select Class');
const SelectGradeOption = getZeroOption('Select Grade');
const SelectExamOption = getZeroOption('Select Assessment');
const AllStudentOption = getZeroOption('All');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Assessments Available');

const requiredFields = [
    'districtId',
    'schoolId',
    'gradeLevelMasterId',
    'classId',
    'examId',
] as const;

function getMissingFields(fs: any): string[] {
    return requiredFields
        .filter(
            (key) =>
                !fs[key] ||
                Number(fs[key]) === Number(SelectDistrictOption.id) ||
                Number(fs[key]) === Number(SelectSchoolOption.id) ||
                Number(fs[key]) === Number(SelectGradeOption.id) ||
                Number(fs[key]) === Number(SelectClassOption.id) ||
                Number(fs[key]) === Number(SelectExamOption.id)
        )
        .map((key) => Constants.fieldLabels[key] || key);
}

const AttemptDetailsFilters: React.FC<AttemptDetailsFiltersProps> = ({
    refreshTableData,
    filterState,
    setFilterState,
}) => {
    const [errorMsg, setErrorMsg] = useState('');
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [classOptions, setClassOptions] = useState<Option[]>([SelectClassOption]);
    const [examOptions, setExamOptions] = useState<Option[]>([SelectExamOption]);
    const [studentOptions, setStudentOptions] = useState<Option[]>([AllStudentOption]);

    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [allExamOptions, setAllExamOptions] = useState<ExamOption[] | null>(null);

    const [filtersLoading, setFiltersLoading] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [examLoading, setExamLoading] = useState(false);
    const [studentLoading, setStudentLoading] = useState(false);

    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true'
                );
                const data = res.data;

                setDistrictOptions([
                    SelectDistrictOption,
                    ...(data.districtOptions ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setGradeLevelMasterOptions([
                    SelectGradeOption,
                    ...(data.gradeLevelMasterOptions ?? []),
                ]);

                if (
                    filterState &&
                    filterState.districtId &&
                    filterState.districtId !== Number(SelectDistrictOption.id)
                ) {
                    await fetchSchoolClassOptions(filterState.districtId);
                }
            } catch {
                setErrorMsg('Failed to load initial filter options');
                setDistrictOptions([SelectDistrictOption]);
                setGradeLevelMasterOptions([SelectGradeOption]);
            } finally {
                setFiltersLoading(false);
                setInitialLoadComplete(true);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!initialLoadComplete) return;

        if (filterState.districtId && filterState.districtId !== Number(SelectDistrictOption.id)) {
            fetchSchoolClassOptions(filterState.districtId).then(() => setFiltersLoading(false));
        } else {
            setFiltersLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoadComplete, filterState.districtId]);

    const fetchSchoolClassOptions = async (districtId: number | string) => {
        if (!districtId || Number(districtId) === Number(SelectDistrictOption.id)) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(SelectSchoolOption.id),
                classId: Number(SelectClassOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;

            const newSchoolOptions = (data.schoolOptions ?? []).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setSchoolOptions([SelectSchoolOption, ...newSchoolOptions]);

            setFilterState((prev) => {
                const schoolExists = newSchoolOptions.some(
                    (opt) => Number(opt.id) === Number(prev.schoolId)
                );
                return {
                    ...prev,
                    schoolId: schoolExists ? prev.schoolId : Number(SelectSchoolOption.id),
                };
            });

            const sortedClassData = (data.classOptions ?? []).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setClassData(sortedClassData);

            setClassOptions([SelectClassOption]);
            setErrorMsg('');
        } catch {
            setErrorMsg('Failed to fetch school and class options.');
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
        }
    };

    useEffect(() => {
        if (
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(classData, filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    const updateClassOptions = (data: ClassOption[], gradeId: number, schoolId: number) => {
        if (
            !schoolId ||
            schoolId === Number(SelectSchoolOption.id) ||
            !gradeId ||
            gradeId === Number(SelectGradeOption.id)
        ) {
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(SelectClassOption.id) }));
            return;
        }

        const newClassOptions = data
            .filter((option) => {
                if (schoolId && option.schoolId !== schoolId) return false;
                const glids = option.gradeLevelMasterIds;
                if (glids && glids.length > 0 && !glids.includes(gradeId)) return false;
                return true;
            })
            .map((option) => ({ id: option.id, name: option.name }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
        } else {
            setClassOptions([SelectClassOption, ...newClassOptions]);
            setFilterState((prev) => {
                const classExists = newClassOptions.some(
                    (c) => Number(c.id) === Number(prev.classId)
                );
                return {
                    ...prev,
                    classId: classExists ? prev.classId : Number(SelectClassOption.id),
                };
            });
        }
    };

    useEffect(() => {
        maybeFetchExamOptions(filterState.gradeLevelMasterId);
    }, [filterState.gradeLevelMasterId]);

    const maybeFetchExamOptions = async (gradeLevelMasterId: number) => {
        if (!gradeLevelMasterId || gradeLevelMasterId === Number(SelectGradeOption.id)) {
            setAllExamOptions(null);
            setExamLoading(false);
            return;
        }

        setExamLoading(true);
        try {
            const res = await apiRequest<{ examOptions: ExamOption[] }>(
                'GET',
                `utility/filterData?getExam=true&gradeLevelMasterIds=${gradeLevelMasterId}&showPublishedExamsOnly=true`
            );
            const data = (res.data.examOptions ?? []).filter((exam) => exam.schoolIds?.length);
            setAllExamOptions(data.sort((a, b) => a.name.localeCompare(b.name)));
        } catch {
            setAllExamOptions([]);
            setErrorMsg('Failed to load assessment options.');
        } finally {
            setExamLoading(false);
        }
    };

    useEffect(() => {
        filterExamsLocally(filterState.schoolId, filterState.classId, allExamOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allExamOptions, filterState.schoolId, filterState.classId]);

    const filterExamsLocally = (
        schoolId: number,
        classId: number,
        allExamsData: ExamOption[] | null
    ) => {
        if (!allExamsData) return;

        if (allExamsData.length === 0) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            return;
        }

        let filtered = allExamsData;

        if (schoolId && schoolId !== Number(SelectSchoolOption.id)) {
            filtered = filtered.filter(
                (e) => !e.schoolIds?.length || e.schoolIds.includes(schoolId)
            );
        }

        if (
            classId &&
            classId !== Number(SelectClassOption.id) &&
            classId !== Number(NoClassOption.id)
        ) {
            filtered = filtered.filter((e) => !e.classIds?.length || e.classIds.includes(classId));
        }

        if (!filtered.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
        } else {
            setExamOptions([SelectExamOption, ...filtered]);
            setFilterState((prev) => {
                const examExists = filtered.some((e) => Number(e.id) === Number(prev.examId));
                return { ...prev, examId: examExists ? prev.examId : Number(SelectExamOption.id) };
            });
        }
    };

    useEffect(() => {
        if (initialLoadComplete) {
            fetchStudentOptions(filterState.classId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoadComplete, filterState.classId]);

    const fetchStudentOptions = async (classId: number) => {
        if (
            !classId ||
            classId === Number(SelectClassOption.id) ||
            classId === Number(NoClassOption.id)
        ) {
            setStudentOptions([AllStudentOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
            return;
        }

        setStudentLoading(true);
        try {
            const res = await apiRequest<{ studentOptions: Option[] }>(
                'GET',
                `utility/filterData?getStudentsByClass=true&classId=${classId}`
            );
            const data = res.data.studentOptions || [];
            setStudentOptions([
                AllStudentOption,
                ...data.sort((a, b) => a.name.localeCompare(b.name)),
            ]);

            setFilterState((prev) => {
                const studentExists = data.some((s) => Number(s.id) === Number(prev.studentId));
                return {
                    ...prev,
                    studentId: studentExists ? prev.studentId : Number(AllStudentOption.id),
                };
            });
        } catch {
            setStudentOptions([AllStudentOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
        } finally {
            setStudentLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newId = Number(e.target.value);
                        setFilterState({
                            ...filterState,
                            districtId: newId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                            examId: Number(SelectExamOption.id),
                            studentId: Number(AllStudentOption.id),
                        });
                        fetchSchoolClassOptions(newId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            schoolId: newId,
                            classId: Number(SelectClassOption.id),
                            examId: Number(SelectExamOption.id),
                            studentId: Number(AllStudentOption.id),
                        }));
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.districtId === Number(SelectDistrictOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            gradeLevelMasterId: newId,
                            classId: Number(SelectClassOption.id),
                            examId: Number(SelectExamOption.id),
                        }));
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class *"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id ?? 0
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            classId: newId,
                            studentId: Number(AllStudentOption.id),
                        }));
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>

            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment *"
                    {...getLoadingProps(
                        examOptions,
                        Number(
                            getValidSelectValue(
                                filterState.examId,
                                examOptions,
                                examOptions[0]?.id ?? 0
                            )
                        ),
                        examLoading || filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, examId: Number(e.target.value) }));
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        examLoading ||
                        filtersLoading ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="student-select"
                    label="Student"
                    {...getLoadingProps(
                        studentOptions,
                        Number(
                            getValidSelectValue(
                                filterState.studentId,
                                studentOptions,
                                studentOptions[0]?.id ?? 0
                            )
                        ),
                        studentLoading || filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, studentId: Number(e.target.value) }));
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        studentLoading ||
                        filtersLoading ||
                        filterState.classId === Number(SelectClassOption.id) ||
                        filterState.classId === Number(NoClassOption.id)
                    }
                />
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setErrorMsg(`Please select: ${missing.join(', ')} before filtering.`);
                            return;
                        }
                        setErrorMsg('');
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center', padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: 16, height: 16, marginRight: 8 }}
                    />
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            <ErrorMessage error={errorMsg} />
        </Box>
    );
};

export default AttemptDetailsFilters;
